import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
	Divider,
	EmptyRow,
	Flex,
	Pagination,
	SelectInput,
	Spinner,
	Status,
	Table,
	Td,
	Tr,
} from '@components'
import type { Option } from '@components/SelectInput'
import type {
	AuditActorType,
	AuditChanges,
	AuditEntityType,
	AuditLog as AuditLogModel,
} from '@models/audit.model'
import type { ShowClientResponse } from '@models/client.model'
import { clientApi } from '@services/ClientApi'
import Formatters from '@utils/helpers/Formatters'
import { useQuery } from '@utils/hooks'

interface AuditLogProps {
	clientInfo?: ShowClientResponse
}

const ACTION_LABELS: Record<string, string> = {
	'client.create': 'Cliente criado',
	'client.register': 'Cliente registrou-se',
	'client.update': 'Dados do cliente atualizados',
	'client.profile.update': 'Perfil atualizado',
	'client.image.update': 'Foto atualizada',
	'client.password.update': 'Senha alterada',
	'client.password.reset': 'Senha redefinida',
	'client.access.grant': 'Acesso concedido',
	'client.delete': 'Cliente removido',
	'enrollment.create': 'Turma adicionada',
	'enrollment.update': 'Turma atualizada',
	'enrollment.delete': 'Turma removida',
}

const ACTOR_LABELS: Record<AuditActorType, string> = {
	admin: 'Admin',
	client: 'Cliente',
	system: 'Sistema',
}

const ACTOR_STATUS: Record<AuditActorType, 'info' | 'success' | 'warn'> = {
	admin: 'info',
	client: 'success',
	system: 'warn',
}

const FIELD_LABELS: Record<string, string> = {
	name: 'Nome',
	email: 'E-mail',
	phone: 'Telefone',
	whatsapp: 'WhatsApp',
	cpfCnpj: 'CPF/CNPJ',
	password: 'Senha',
	imageUrl: 'Foto',
	cep: 'CEP',
	city: 'Cidade',
	district: 'Bairro',
	street: 'Rua',
	number: 'Número',
	uf: 'UF',
	classId: 'Turma',
	orderId: 'Pedido',
	expirationDate: 'Data de expiração',
	isRefunded: 'Estornado',
	isCanceled: 'Cancelado',
	cancellationReason: 'Motivo do cancelamento',
	bypassRateLimit: 'Ignorar limite',
	exceededWeeklyDownloadLimit: 'Excedeu limite semanal',
}

const styles = {
	filters: 'gap-3 mb-6 mt-6 flex-wrap items-center',
	filterItem: 'min-w-[180px]',
	row: 'border-b border-gray-100',
	cell: 'px-6 py-5 align-top',
	changes: 'flex flex-col gap-2',
	changeRow: 'flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm',
	field: 'text-gray-500',
	value: 'flex flex-wrap items-baseline gap-2',
	old: 'text-red line-through',
	arrow: 'text-gray-400',
	new: 'text-gray-800 font-medium',
	none: 'text-gray-400 italic',
	meta: 'text-xs text-gray-400',
	actorCell: 'flex-col items-start gap-1',
	actorName: 'text-sm font-medium text-gray-700',
	divider: 'mt-[32px] mb-[12px]',
}

const ENTITY_OPTIONS: Option[] = [
	{ option: 'Todas as entidades', value: '' },
	{ option: 'Cliente', value: 'client' },
	{ option: 'Turma', value: 'enrollment' },
]

const ACTOR_OPTIONS: Option[] = [
	{ option: 'Todos os autores', value: '' },
	{ option: 'Admin', value: 'admin' },
	{ option: 'Cliente', value: 'client' },
	{ option: 'Sistema', value: 'system' },
]

function fieldLabel(field: string) {
	return FIELD_LABELS[field] || field
}

function isEmpty(value: unknown) {
	return value === null || value === undefined || value === ''
}

function renderValue(value: unknown) {
	if (isEmpty(value)) return '—'
	if (typeof value === 'boolean') return value ? 'Sim' : 'Não'

	return String(value)
}

function Changes({ changes }: { changes: AuditChanges | null }) {
	if (!changes || Object.keys(changes).length === 0) {
		return <span className={styles.none}>Sem alterações de campo</span>
	}

	return (
		<div className={styles.changes}>
			{Object.entries(changes).map(([field, change]) => {
				const hadOld = !isEmpty(change?.old)

				return (
					<div key={field} className={styles.changeRow}>
						<span className={styles.field}>{fieldLabel(field)}</span>
						<span className={styles.value}>
							{hadOld && (
								<>
									<span className={styles.old}>{renderValue(change?.old)}</span>
									<span className={styles.arrow}>→</span>
								</>
							)}
							<span className={styles.new}>{renderValue(change?.new)}</span>
						</span>
					</div>
				)
			})}
		</div>
	)
}

export function AuditLog({ clientInfo }: AuditLogProps) {
	const query = useQuery()
	const navigate = useNavigate()
	const { pathname } = useLocation()

	const page = Number(query.get('page')) || 1

	const [entityType, setEntityType] = useState<AuditEntityType | ''>('')
	const [actorType, setActorType] = useState<AuditActorType | ''>('')

	const { data, isLoading, isFetching } = clientApi.useListAuditLogsQuery(
		{
			clientId: clientInfo?.id || '',
			page,
			limit: 20,
			entityType,
			actorType,
		},
		{ skip: !clientInfo?.id }
	)

	function resetPage() {
		if (page !== 1) navigate(`${pathname}?page=1`)
	}

	const logs = data?.data || []

	return (
		<>
			<Flex className={styles.filters}>
				<div className={styles.filterItem}>
					<SelectInput
						options={ENTITY_OPTIONS}
						selectedValue={entityType}
						onChange={(option) => {
							setEntityType(option.value as AuditEntityType | '')
							resetPage()
						}}
					/>
				</div>
				<div className={styles.filterItem}>
					<SelectInput
						options={ACTOR_OPTIONS}
						selectedValue={actorType}
						onChange={(option) => {
							setActorType(option.value as AuditActorType | '')
							resetPage()
						}}
					/>
				</div>
			</Flex>

			{isLoading || isFetching ? (
				<div className='flex justify-center'>
					<Spinner size='md' />
				</div>
			) : (
				<Table
					th={[
						{ label: 'data/hora' },
						{ label: 'autor' },
						{ label: 'ação' },
						{ label: 'alterações' },
					]}
				>
					{logs.length > 0 ? (
						logs.map((log: AuditLogModel) => (
							<Tr key={log.id} className={styles.row}>
								<Td className={`w-[18%] ${styles.cell}`}>
									{Formatters.createdAt(log.createdAt, 3)}
								</Td>
								<Td className={`w-[20%] ${styles.cell}`}>
									<Flex className={styles.actorCell}>
										<Status
											className='px-3 py-0.5 text-xs'
											status={ACTOR_STATUS[log.actorType] || 'info'}
										>
											{ACTOR_LABELS[log.actorType] || log.actorType}
										</Status>
										{log.actorName && (
											<span className={styles.actorName}>{log.actorName}</span>
										)}
										{log.ipAddress && (
											<span className={styles.meta}>IP: {log.ipAddress}</span>
										)}
									</Flex>
								</Td>
								<Td className={`w-[22%] ${styles.cell}`}>
									{ACTION_LABELS[log.action] || log.action}
								</Td>
								<Td className={`w-[40%] ${styles.cell}`}>
									<Changes changes={log.changes} />
								</Td>
							</Tr>
						))
					) : (
						<EmptyRow tdAmount={4} message='Nenhum registro de auditoria' />
					)}
				</Table>
			)}

			<Divider className={styles.divider} />
			{logs.length > 0 && (
				<Pagination
					pageCount={data?.meta.lastPage || 0}
					maxAmount={data?.meta.total || 0}
					perPage={data?.meta.perPage || 0}
				/>
			)}
		</>
	)
}
